import React, { useState, useEffect } from "react";
import { Button, Text, Icon, IconName, Box, RadioCardGroup } from "@telia/teddy";
import format from "date-fns/format";
import _isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";
import { HolidayData, RootState } from "../../../../../models";
import { getDefaultPortinDate, getEarliestPossiblePortingDate } from "../../../../../shared/util";
import { Alert } from "../../../components";
import PortingCard from "../../../update-porting-date/components/molecules/PortingCard/PortingCard";
import { getHolidayData, updatePortinDate } from "../../state/actions/addMemberActions";
import { isSubscriptionDraft as isSubscriptionDraftSelector } from "../../state/selectors/cartSelectors";
import { isEsimSelected } from "../../state/selectors/customerInfoSelectors";
import { getSelectedPhoneNumber } from "../../state/selectors/phoneNumberSelectors";
import { getSelectedPortinDate } from "../../state/selectors/subscriptionsSelectors";

interface Props {
    onContinue: () => void;
    subscriptionId: string;
}

type PortingDateSelection = "asap" | "custom";
const ASAP_DATE_UPDATED_MESSAGE = "Overføringstidspunktet er oppdatert. Se over tidspunktet og prøv igjen.";

const MemberPortingCard: React.FC<Props> = ({ onContinue, subscriptionId }) => {
    const [holidayData, setHolidayData] = useState<HolidayData | undefined>(undefined);
    const isEsim = useSelector((state: RootState) => isEsimSelected(state));
    const portinDateFromBackend = useSelector(
        (state: RootState) => getSelectedPortinDate(state, subscriptionId) || getDefaultPortinDate(isEsim),
    );
    const [selectedDate, setSelectedDate] = useState(new Date(portinDateFromBackend || ""));
    const dispatch = useDispatch();
    const productUserPhoneNumber = useSelector((state: RootState) => getSelectedPhoneNumber(state, subscriptionId));
    const isSubscriptionDraft = useSelector((state: RootState) => isSubscriptionDraftSelector(state, subscriptionId));
    const [asapDate, setAsapDate] = useState(() => getEarliestPossiblePortingDate());
    const [portingDateSelection, setPortingDateSelection] = useState<PortingDateSelection>(isEsim ? "asap" : "custom");
    const [showAsapDateUpdatedMessage, setShowAsapDateUpdatedMessage] = useState(false);

    // Keep the estimate fresh in case the user stays on this page across a minute boundary
    useEffect(() => {
        const interval = setInterval(() => setAsapDate(getEarliestPossiblePortingDate()), 10_000);
        return () => clearInterval(interval);
    }, []);

    const buttonLabel = isSubscriptionDraft ? "Fortsett" : "Lagre";
    const buttonIcon = isSubscriptionDraft ? "arrow-down" : "check-mark";
    const buttonIconPlacement = isSubscriptionDraft ? "right" : "left";

    const handleOnContinue = () => {
        const latestAsapDate = getEarliestPossiblePortingDate();

        if (isEsim && portingDateSelection === "asap" && latestAsapDate.getTime() !== asapDate.getTime()) {
            setAsapDate(latestAsapDate);
            setShowAsapDateUpdatedMessage(true);
            return;
        }

        setShowAsapDateUpdatedMessage(false);
        const dateToUse = isEsim && portingDateSelection === "asap" ? latestAsapDate : selectedDate;
        const selectedDateISOString = dateToUse.toISOString();
        if (selectedDateISOString !== portinDateFromBackend) {
            dispatch(updatePortinDate(selectedDateISOString, subscriptionId));
        }

        onContinue();
    };

    useEffect(() => {
        if (_isEmpty(holidayData)) {
            dispatch(getHolidayData()).then((data) => setHolidayData(data.holidayData));
        }
    }, []);

    const estimatedDateLabel = format(asapDate, "dd.MM.yyyy 'kl.' HH:mm");

    return (
        <div>
            {isEsim && (
                <RadioCardGroup
                    value={portingDateSelection}
                    onValueChange={(value) => {
                        setPortingDateSelection(value as PortingDateSelection);
                        setShowAsapDateUpdatedMessage(false);
                    }}
                    orientation="vertical"
                    name="porting-date-selection"
                >
                    <RadioCardGroup.Item value="asap">
                        <RadioCardGroup.Title>Så fort som mulig</RadioCardGroup.Title>
                        <RadioCardGroup.Body>
                            Estimert: <span className="telia-sans-medium">{estimatedDateLabel}</span>
                        </RadioCardGroup.Body>
                    </RadioCardGroup.Item>
                    <RadioCardGroup.Item value="custom">
                        <RadioCardGroup.Title>Velg et tidspunkt</RadioCardGroup.Title>
                    </RadioCardGroup.Item>
                </RadioCardGroup>
            )}
            {showAsapDateUpdatedMessage && (
                <Box mt="200">
                    <Alert kind="info">{ASAP_DATE_UPDATED_MESSAGE}</Alert>
                </Box>
            )}
            {portingDateSelection === "custom" && (
                <Box mt="200">
                    {!isEsim && (
                        <Text ml="100" mb="100" variant="additional-100">
                            Velg tidspunktet som passer deg for å ta i bruk ditt nye SIM-kort. SIM-kortet leveres i din
                            postkasse 5–10 dager etter bestilling.
                        </Text>
                    )}
                    <PortingCard
                        // TODO: Don't hardcode props and send to backend on setSelectedDate
                        // oxlint-disable-next-line no-console
                        selectedDate={selectedDate}
                        setSelectedDate={(date) => setSelectedDate(date)}
                        phoneNumber={productUserPhoneNumber}
                        isAllowedToUpdate={true}
                        holidayData={holidayData || {}}
                        shouldShowSameCheckBoxOnChanged={false}
                        shouldShowSameCheckBoxValue={false}
                        creationDate={new Date().toISOString()}
                        isEsim={isEsim}
                    />
                </Box>
            )}
            <Text ml="100" variant="additional-100" mt="200">
                Du kan endre denne datoen senere. Du vil motta en SMS med mer informasjon om denne muligheten.
            </Text>
            <div className="add-member__step-button-container">
                <Button onClick={() => handleOnContinue()} className="add-member__step-button">
                    {buttonIconPlacement === "left" && (
                        <Icon className="add-member__step-button-icon" name={buttonIcon as IconName} />
                    )}
                    {buttonLabel}
                    {buttonIconPlacement === "right" && (
                        <Icon className="add-member__step-button-icon" name={buttonIcon as IconName} />
                    )}
                </Button>
            </div>
        </div>
    );
};

export default MemberPortingCard;
